/**
 * Hook for the Email-Forward inbox: list, reparse, and assign-to-trip.
 *
 * Mirrors the trip list hook's shape: a React Query wrapper with a stable
 * return surface that pages (TravelInboxPage) can consume without having to
 * know about React Query internals.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type { TravelInboxListResponse, TravelInboundEmailResponse } from '../api';
import { queryKeys } from '@/lib/query-keys';

interface UseTravelInboxOptions {
  limit?: number;
  offset?: number;
}

export function useTravelInbox(options: UseTravelInboxOptions = {}) {
  const queryClient = useQueryClient();
  const key = queryKeys.travel.inbox(options as Record<string, unknown>);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: key,
    queryFn: () => travelApi.listTravelInboundEmails(options),
  });

  const reparseMut = useMutation({
    mutationFn: (emailId: string) => travelApi.reparseTravelInboundEmail(emailId),
    onSuccess: (res) => {
      queryClient.setQueryData<TravelInboxListResponse | undefined>(key, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          emails: prev.emails.map((e) => (e.emailId === res.email.emailId ? res.email : e)),
        };
      });
      // Trip detail pages may have picked up new items — invalidate broadly.
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.details() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.my() });
    },
  });

  const assignMut = useMutation({
    mutationFn: (vars: { emailId: string; tripId: string }) =>
      travelApi.assignTravelInboundEmailToTrip(vars.emailId, vars.tripId),
    onSuccess: (res) => {
      queryClient.setQueryData<TravelInboxListResponse | undefined>(key, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          emails: prev.emails.map((e) => (e.emailId === res.email.emailId ? res.email : e)),
        };
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.travel.details() });
    },
  });

  return {
    emails: (data?.emails ?? []) as TravelInboundEmailResponse[],
    total: data?.total ?? 0,
    featureEnabled: data?.featureEnabled ?? true,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refresh: refetch,
    reparse: (emailId: string) => reparseMut.mutateAsync(emailId),
    isReparsing: reparseMut.isPending,
    assignToTrip: (emailId: string, tripId: string) => assignMut.mutateAsync({ emailId, tripId }),
    isAssigning: assignMut.isPending,
  };
}
